import { usePageHeader } from "contexts/PageHeaderContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import {
  RefreshCw,
  Search,
  Eye,
  Filter,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Input } from '../../ui/input';
import { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';

interface TORegistrationRenewalListProps {
  onNavigate: (page: string) => void;
}

interface RenewalRequest {
  id: string;
  tourOperatorName: string;
  officeName: string;
  itsNumber: string;
  currentExpiryDate: string;
  requestedDate: string;
  status: 'pending' | 'approved' | 'rejected';
  daysUntilExpiry: number;
}

// Mock data
const mockRenewals: RenewalRequest[] = [
  {
    id: '1',
    tourOperatorName: 'Fatima Tours & Travels',
    officeName: 'Mumbai Main Office',
    itsNumber: '12345678',
    currentExpiryDate: '2025-02-28',
    requestedDate: '2024-12-01',
    status: 'pending',
    daysUntilExpiry: 75
  },
  {
    id: '2',
    tourOperatorName: 'Al-Khair Travel Agency',
    officeName: 'Delhi Regional Office',
    itsNumber: '87654321',
    currentExpiryDate: '2025-03-15',
    requestedDate: '2024-12-05',
    status: 'approved',
    daysUntilExpiry: 90
  },
  {
    id: '3',
    tourOperatorName: 'Hussaini Travels',
    officeName: 'Bangalore Branch',
    itsNumber: '11223344',
    currentExpiryDate: '2025-01-31',
    requestedDate: '2024-11-28',
    status: 'pending',
    daysUntilExpiry: 47
  },
  {
    id: '4',
    tourOperatorName: 'Zahra Travel Services',
    officeName: 'Hyderabad Office',
    itsNumber: '99887766',
    currentExpiryDate: '2025-02-15',
    requestedDate: '2024-12-10',
    status: 'rejected',
    daysUntilExpiry: 62
  }
];

export default function TORegistrationRenewalList({ onNavigate }: TORegistrationRenewalListProps) {
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: "Tour Operator Renewal Requests",
      childTitle: "Manage and review tour operator registration renewals",
      icon: <RefreshCw className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Filter states (matching Venue Master)
  const [filters, setFilters] = useState({
    toName: '',
    itsNumber: '',
    officeName: '',
    status: 'all',
    expiryRange: 'all'
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-green-500 text-white">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-orange-500 text-white">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-500 text-white">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return null;
    }
  };

  const filteredRenewals = mockRenewals.filter(renewal => {
    // Search term filter
    const matchesSearch =
      renewal.tourOperatorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      renewal.itsNumber.includes(searchTerm) ||
      renewal.officeName.toLowerCase().includes(searchTerm.toLowerCase());

    // Advanced filters
    const matchesToName = !filters.toName || renewal.tourOperatorName.toLowerCase().includes(filters.toName.toLowerCase());
    const matchesItsNumber = !filters.itsNumber || renewal.itsNumber.includes(filters.itsNumber);
    const matchesOfficeName = !filters.officeName || renewal.officeName.toLowerCase().includes(filters.officeName.toLowerCase());
    const matchesStatus = filters.status === 'all' || renewal.status === filters.status;
    const matchesExpiryRange = filters.expiryRange === 'all' ||
      (filters.expiryRange === 'critical' && renewal.daysUntilExpiry <= 30) ||
      (filters.expiryRange === 'warning' && renewal.daysUntilExpiry > 30 && renewal.daysUntilExpiry <= 60) ||
      (filters.expiryRange === 'safe' && renewal.daysUntilExpiry > 60);

    return matchesSearch && matchesToName && matchesItsNumber && matchesOfficeName && matchesStatus && matchesExpiryRange;
  });

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn('');
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Apply sorting
  const sortedRenewals = [...filteredRenewals].sort((a, b) => {
    if (!sortColumn) return 0;

    let aValue: string | number = "";
    let bValue: string | number = "";

    if (sortColumn === "tourOperatorName") {
      aValue = a.tourOperatorName;
      bValue = b.tourOperatorName;
    } else if (sortColumn === "itsNumber") {
      aValue = a.itsNumber;
      bValue = b.itsNumber;
    } else if (sortColumn === "currentExpiryDate") {
      aValue = new Date(a.currentExpiryDate).getTime();
      bValue = new Date(b.currentExpiryDate).getTime();
    } else if (sortColumn === "daysUntilExpiry") {
      aValue = a.daysUntilExpiry;
      bValue = b.daysUntilExpiry;
    } else if (sortColumn === "status") {
      aValue = a.status;
      bValue = b.status;
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });


  // Render sort icon
  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-0 group-hover:opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-4 h-4 ml-1 inline" />
      : <ArrowDown className="w-4 h-4 ml-1 inline" />;
  };

  // Clear all filters (matching Venue Master)
  const handleClearFilters = () => {
    setFilters({
      toName: '',
      itsNumber: '',
      officeName: '',
      status: 'all',
      expiryRange: 'all'
    });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-4">

        <Button
          variant="outline"
          onClick={() => setShowStatistics(!showStatistics)}
          className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
        >
          {showStatistics ? <ArrowUp className="w-4 h-4 mr-2" /> : <ArrowDown className="w-4 h-4 mr-2" />}
          Statistics
        </Button>
      </div>

      {/* Statistics Cards (Collapsible) */}
      {showStatistics && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-[#5B9BD5]/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Requests</p>
                  <p className="text-2xl font-bold text-gray-900">{mockRenewals.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E3F2FD' }}>
                  <RefreshCw className="w-6 h-6 text-[#5B9BD5]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200 bg-orange-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600">Pending</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {mockRenewals.filter(r => r.status === 'pending').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600">Approved</p>
                  <p className="text-2xl font-bold text-green-900">
                    {mockRenewals.filter(r => r.status === 'approved').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Due Soon</p>
                  <p className="text-2xl font-bold text-red-900">
                    {mockRenewals.filter(r => r.daysUntilExpiry <= 30).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search Bar and Filter Toggle */}
      <Card className="border-[#5B9BD5]/30">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by TO Name, ITS Number, or Office Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showFilters ? "default" : "outline"}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              style={showFilters ? { background: '#5B9BD5' } : {}}
              className={showFilters ? "text-white" : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"}
              title="Toggle Advanced Filters"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Section (Venue Master Style) */}
      {showFilters && (
        <Card className="border-[#5B9BD5]/30 bg-gradient-to-br from-sky-50/50 to-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle style={{ color: '#5B9BD5' }} className="flex items-center gap-2 text-lg">
                  <Filter className="w-5 h-5" />
                  Advanced Filters
                </CardTitle>
                <CardDescription className="mt-1">
                  Filter renewal requests by multiple criteria
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* TO Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-toname" className="text-sm">Tour Operator Name</Label>
                <Input
                  id="filter-toname"
                  placeholder="Search by TO name..."
                  value={filters.toName}
                  onChange={(e) => setFilters({ ...filters, toName: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              {/* ITS Number Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-its" className="text-sm">ITS Number</Label>
                <Input
                  id="filter-its"
                  placeholder="Search by ITS number..."
                  value={filters.itsNumber}
                  onChange={(e) => setFilters({ ...filters, itsNumber: e.target.value })}
                  className="font-mono h-9 text-sm"
                />
              </div>

              {/* Office Name Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-office" className="text-sm">Office Name</Label>
                <Input
                  id="filter-office"
                  placeholder="Search by office name..."
                  value={filters.officeName}
                  onChange={(e) => setFilters({ ...filters, officeName: e.target.value })}
                  className="h-9 text-sm"
                />
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-status" className="text-sm">Status</Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger id="filter-status" className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending Only</SelectItem>
                    <SelectItem value="approved">Approved Only</SelectItem>
                    <SelectItem value="rejected">Rejected Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Expiry Range Filter */}
              <div className="space-y-2">
                <Label htmlFor="filter-expiry" className="text-sm">Expiry Range</Label>
                <Select value={filters.expiryRange} onValueChange={(value) => setFilters({ ...filters, expiryRange: value })}>
                  <SelectTrigger id="filter-expiry" className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ranges</SelectItem>
                    <SelectItem value="critical">Critical (≤ 30 days)</SelectItem>
                    <SelectItem value="warning">Warning (31-60 days)</SelectItem>
                    <SelectItem value="safe">Safe (&gt; 60 days)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Active Filters Summary & Clear Button (Venue Master Style) */}
            {(filters.toName || filters.itsNumber || filters.officeName || filters.status !== 'all' || filters.expiryRange !== 'all') && (
              <div className="flex items-center justify-between gap-3 p-3 bg-white border border-[#5B9BD5]/30 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#5B9BD5] mb-2">Active Filters:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {filters.toName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        TO: {filters.toName}
                      </Badge>
                    )}
                    {filters.itsNumber && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        ITS: {filters.itsNumber}
                      </Badge>
                    )}
                    {filters.officeName && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Office: {filters.officeName}
                      </Badge>
                    )}
                    {filters.status !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Status: {filters.status}
                      </Badge>
                    )}
                    {filters.expiryRange !== 'all' && (
                      <Badge variant="outline" className="border-[#5B9BD5]/30 text-[#5B9BD5] text-xs">
                        Expiry: {filters.expiryRange}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                  className="border-red-300 text-red-600 hover:bg-red-50 flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Renewal List */}
      <Card className="border-[#5B9BD5]/20">
        <CardHeader>
          <CardTitle className="text-gray-900">Renewal Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full">
              <thead>
                <tr style={{ backgroundColor: '#E3F2FD' }}>
                  <th
                    className="px-4 py-3 text-left text-xs cursor-pointer hover:bg-sky-100 transition-colors group"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('tourOperatorName')}
                  >
                    Tour Operator / Office Name
                    {renderSortIcon('tourOperatorName')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs cursor-pointer hover:bg-sky-100 transition-colors group"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('itsNumber')}
                  >
                    ITS Number
                    {renderSortIcon('itsNumber')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs cursor-pointer hover:bg-sky-100 transition-colors group"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('currentExpiryDate')}
                  >
                    Current Expiry
                    {renderSortIcon('currentExpiryDate')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs cursor-pointer hover:bg-sky-100 transition-colors group"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('daysUntilExpiry')}
                  >
                    Days Until Expiry
                    {renderSortIcon('daysUntilExpiry')}
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs cursor-pointer hover:bg-sky-100 transition-colors group"
                    style={{ color: '#5B9BD5' }}
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {renderSortIcon('status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs" style={{ color: '#5B9BD5' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {sortedRenewals.map((renewal) => (
                  <tr key={renewal.id} className="hover:bg-sky-50/30">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-gray-300 mt-1.5 flex-shrink-0"></div>
                        <div>
                          <p className="text-gray-900 text-sm font-medium">{renewal.tourOperatorName}</p>
                          <p className="text-gray-600 text-xs mt-0.5">{renewal.officeName}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 text-sm font-mono">{renewal.itsNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-900 text-sm">
                        {new Date(renewal.currentExpiryDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        className={`text-xs px-2 py-1 ${renewal.daysUntilExpiry <= 30
                          ? 'bg-red-100 text-red-700'
                          : renewal.daysUntilExpiry <= 60
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                          }`}
                      >
                        {renewal.daysUntilExpiry} days
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(renewal.status)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onNavigate('/registration/renewal-details/123')}
                        className="p-1.5 rounded hover:bg-sky-50 transition-colors"
                        style={{ color: '#5B9BD5' }}
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {sortedRenewals.map((renewal) => (
              <div key={renewal.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">{renewal.tourOperatorName}</h4>
                    <p className="text-xs text-gray-600 mt-0.5">{renewal.officeName}</p>
                    <p className="text-xs text-gray-600 font-mono mt-1">ITS: {renewal.itsNumber}</p>
                  </div>
                  {getStatusBadge(renewal.status)}
                </div>
                <div className="space-y-2 mb-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Expiry Date:</span>
                    <span className="text-gray-900">
                      {new Date(renewal.currentExpiryDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Days Left:</span>
                    <Badge
                      className={`text-[10px] px-1.5 py-0.5 ${renewal.daysUntilExpiry <= 30
                        ? 'bg-red-100 text-red-700'
                        : renewal.daysUntilExpiry <= 60
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-green-100 text-green-700'
                        }`}
                    >
                      {renewal.daysUntilExpiry} days
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border hover:bg-gray-50 rounded-lg px-2.5 py-1.5 h-auto text-xs"
                  style={{ borderColor: '#5B9BD5', color: '#5B9BD5' }}
                  onClick={() => onNavigate('/registration/renewal-details/123')}
                >
                  <Eye className="w-3.5 h-3.5 mr-1" />
                  View Details
                </Button>
              </div>
            ))}
          </div>

          {sortedRenewals.length === 0 && (
            <div className="text-center py-12">
              <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No renewal requests found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
